import React from 'react';
import { cn } from '@hero/web-lib';

export interface TableEmptyStateProps {
  /** Icon (typically from @hero/web-icons) rendered inside the rounded chip. */
  icon?: React.ReactNode;
  /** Primary heading, e.g. "No customers yet". */
  title: string;
  /** Supporting copy under the title. */
  description?: string;
  /** Optional CTA — usually a <ButtonCreateEntity ... /> or any button. */
  action?: React.ReactNode;
  /** Extra classes for the outer container. */
  className?: string;
  /**
   * Visual size. `compact` is used inside table bodies (no card chrome),
   * `card` adds a dashed bordered card — useful as a standalone block.
   */
  variant?: 'compact' | 'card';
}

/**
 * Reusable empty / placeholder state used by `SharedTable` (and any other
 * surface that needs the same "icon → title → description → CTA" pattern).
 *
 * Lives at the `app/` tier so it can be composed by feature tables and pages
 * without pulling in domain knowledge.
 */
export function TableEmptyState({
  icon,
  title,
  description,
  action,
  className,
  variant = 'compact',
}: TableEmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 py-16 px-6 text-center',
        variant === 'card' && 'rounded-xl border border-dashed border-gray-200 bg-white',
        className,
      )}>
      {icon && (
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      {description && <p className="max-w-md text-sm text-gray-500">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
